import { Capacitor } from "@capacitor/core";
import { useNavigate } from "react-router-dom";
import { useIapEnabled } from "@/hooks/useIapEnabled";
import { useAuth } from "@/contexts/AuthContext";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { getBannerPreviewData, clearBannerPreview } from "@/lib/dunningBannerPreview";
import { X } from "lucide-react";
import { usePermissions } from "@/hooks/usePermissions";
import {
  getDunningBannerView,
  type DunningMessageOverrides,
  type DunningPlatform,
} from "@/lib/dunningBanner";
import { useDunningMessages } from "@/hooks/useDunningMessages";
import { TierBadge, type SubscriptionTier } from "@/components/ui/tier-badge";
import { useSubscriptionProvider } from "@/hooks/useSubscriptionProvider";
import { openExternalUrl } from "@/utils/linkify";

// Per-tone styling. The decision of WHICH tone (and all copy / href logic)
// lives in getDunningBannerView; this map only carries presentation.
// The tone lives in the tint and border only. Tone-coloured text measured
// 2:1 (amber) and 3.3:1 (red) on its own tint, and white on the amber/red
// fill 2.1:1 and 3.8:1. The message is foreground text and the CTA a white
// pill with foreground text, both far above 4.5:1, and the CTA is a 44px
// target with a primary-strong focus ring.
const MESSAGE_CLASS = "text-sm text-foreground";
const BUTTON_CLASS =
  "shrink-0 min-h-11 rounded-lg border border-border bg-background px-3 text-sm font-semibold text-foreground shadow-sm active:scale-[0.98] active:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--primary-strong))] focus-visible:ring-offset-2";

const TONE_CLASSES = {
  destructive: { alert: "border-destructive/40 bg-destructive/10" },
  warning: { alert: "border-warning/50 bg-warning/10" },
} as const;

export interface SubscriptionBannerData {
  subscriptionStatus?: string | null;
  subscriptionDaysRemaining?: number | null;
  subscriptionRetryCount?: number | null;
  subscriptionMaxRetries?: number | null;
  subscriptionDeclineType?: string | null;
  subscriptionStatusUrl?: string | null;
  subscriptionTier?: SubscriptionTier | null;
  /** profiles.subscription_provider — decides the native CTA (see dunningBanner). */
  subscriptionProvider?: string | null;
  /** Store purchases live on this platform — enables the in-app paywall CTA. */
  iapEnabled?: boolean;
  /** Admin-edited wording (banner_config) — see useDunningMessages. */
  messageOverrides?: DunningMessageOverrides;
}

// The statuses getDunningBannerView renders a banner for.
const BANNER_STATUSES = ["past_due", "on_hold", "pending_cancellation"];

/** Renders banner UI from explicit props — used by both production and admin preview */
export function SubscriptionBannerContent(props: SubscriptionBannerData) {
  // Store anti-steering (App Store 3.1.1; Play Payments policy): a native build
  // must NOT surface a CTA leading to an external web payment flow for in-app
  // digital content. getDunningBannerView therefore returns a null button on
  // native for web-billed members — the banner then renders text-only — and the
  // store's own subscription screen for Apple/Google-billed members. Web is
  // unchanged. Revisit if/when the External Link Account (reader) entitlement is
  // granted for our regions.
  const platform = Capacitor.getPlatform() as DunningPlatform;
  const view = getDunningBannerView({ ...props, platform });
  const navigate = useNavigate();

  if (!view) return null;

  const classes = TONE_CLASSES[view.tone];
  const cta = view.button;

  return (
    <div className="px-4 pt-2">
      <Alert className={classes.alert}>
        <AlertDescription className="flex items-center justify-between gap-3">
          <span className={`flex min-w-0 items-center gap-2 ${MESSAGE_CLASS}`}>
            {props.subscriptionTier && (
              <TierBadge tier={props.subscriptionTier} size="xs" />
            )}
            <span className="min-w-0">{view.message}</span>
          </span>
          {/* No button on native for a web-billed member — nothing we could link
              to is permitted, so the message stands on its own. */}
          {cta && (
            <button
              // openExternalUrl (Capacitor Browser.open on native) — a bare
              // window.open to a cross-origin host is swallowed in the WebView.
              onClick={() => (cta.internal ? navigate(cta.href) : void openExternalUrl(cta.href))}
              className={BUTTON_CLASS}
            >
              {cta.label}
            </button>
          )}
        </AlertDescription>
      </Alert>
    </div>
  );
}

const SubscriptionBanner = () => {
  const { user } = useAuth();
  const { canAccessAdminPages } = usePermissions();
  // Fetched here rather than in SubscriptionBannerContent so the admin preview
  // path (which renders Content directly with mock data) stays hook-free.
  const subscriptionProvider = useSubscriptionProvider();
  const { enabled: iapEnabled } = useIapEnabled();

  // Admin preview mode — override with mock data
  const previewData = user && canAccessAdminPages() ? getBannerPreviewData() : null;
  const shownStatus = previewData?.subscriptionStatus ?? user?.subscriptionStatus;
  const messageOverrides = useDunningMessages(
    !!shownStatus && BANNER_STATUSES.includes(shownStatus),
  );

  if (!user) return null;

  if (previewData) {
    return (
      <div className="relative">
        <div className="absolute top-1 end-5 z-10 flex items-center gap-1 bg-primary text-primary-foreground text-xs font-bold px-2 py-0.5 rounded-full">
          <span>PREVIEW</span>
          <button
            onClick={() => {
              clearBannerPreview();
              window.location.reload();
            }}
            className="active:opacity-70"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
        <SubscriptionBannerContent {...previewData} messageOverrides={messageOverrides} />
      </div>
    );
  }

  return (
    <SubscriptionBannerContent
      subscriptionStatus={user.subscriptionStatus}
      subscriptionDaysRemaining={user.subscriptionDaysRemaining}
      subscriptionRetryCount={user.subscriptionRetryCount}
      subscriptionMaxRetries={user.subscriptionMaxRetries}
      subscriptionDeclineType={user.subscriptionDeclineType}
      subscriptionStatusUrl={user.subscriptionStatusUrl}
      subscriptionTier={user.subscriptionTier ?? null}
      subscriptionProvider={subscriptionProvider}
      iapEnabled={iapEnabled}
      messageOverrides={messageOverrides}
    />
  );
};

export default SubscriptionBanner;
